/**
 * Перенос `lib/src/utils/exceptions/failure_catalog.dart`.
 *
 * Сгенерированный класс `AppLocalizations` заменён объектом [L10n] с тем же
 * плоским доступом по ключам, поэтому подписи функций совпадают с Dart:
 * переводы приходят вторым аргументом, а не берутся изнутри.
 */

import type { L10n, L10nKey } from '@/l10n/types';
import { FailureKind, FailureOp, type AppFailure } from '@/utils/exceptions/appFailure';

/**
 * Что показать посетителю и что подсказать инженеру по каждому сбою.
 *
 * Формулировка берётся по паре «операция + причина». Базовый текст задаётся
 * причиной, а для операций, где он звучит неуместно, стоит переопределение:
 * посетителю, который говорил в микрофон, «сервер недоступен» ничего не даёт,
 * ему нужно «не расслышал, попробуйте ещё раз».
 */

/** Короткая человеческая фраза. Технических подробностей здесь нет никогда. */
export function message(failure: AppFailure, l10n: L10n): string {
  const byOp = byOperation(failure, l10n);
  if (byOp !== null) return byOp;
  return byKind(failure.kind, l10n);
}

/**
 * Имеет ли смысл кнопка «Повторить».
 *
 * Нет смысла там, где повтор ничего не изменит: не выдано разрешение
 * на микрофон, сервер не принял запрос по контракту, запрос отменили сами.
 */
export function canRetry(failure: AppFailure): boolean {
  switch (failure.kind) {
    case FailureKind.badRequest:
    case FailureKind.badContract:
    case FailureKind.cancelled:
      return false;
    case FailureKind.device:
    case FailureKind.notConfigured:
      return false;
    case FailureKind.noNetwork:
    case FailureKind.offline:
    case FailureKind.connectTimeout:
    case FailureKind.sendTimeout:
    case FailureKind.receiveTimeout:
    case FailureKind.serverDown:
    case FailureKind.streamBroken:
    case FailureKind.streamSilent:
    case FailureKind.unknown:
      return true;
  }
}

/**
 * Что проверять инженеру. По-русски, в отчёт и в лог — не на экран киоска.
 *
 * Название операции ставится отдельной строкой после метки, а не внутрь
 * фразы: иначе подстановка ломает падежи.
 */
export function hint(failure: AppFailure): string {
  const what = HINT_BY_KIND[failure.kind](failure);
  return `${what}\nЗапрос: ${ENDPOINT_BY_OP[failure.op]}\nКод: ${failure.code}`;
}

/** 401 — ключ не принят: его нет или он устарел. */
const UNAUTHORIZED_STATUS = 401;

/** 403 — сервер отверг ключ STT либо пароль планшета. */
const FORBIDDEN_STATUS = 403;

/** 429 — исчерпан лимит попыток входа. */
const TOO_MANY_REQUESTS_STATUS = 429;

/**
 * 401/403 у распознавания речи — это не «проверьте путь и тело», а протухший
 * ключ, поэтому у общего текста про 4xx есть отдельная ветка.
 */
function badRequestHint(failure: AppFailure): string {
  const status = failure.statusCode;
  if (
    failure.op === FailureOp.transcribe &&
    (status === UNAUTHORIZED_STATUS || status === FORBIDDEN_STATUS)
  ) {
    return (
      `STT-сервис отверг ключ (${status}): api_key нет или он ` +
      'устарел. Ключ приходит из tablets/config по токену планшета — ' +
      'если сессия истекла (вход по PIN без пароля, прошло 24 часа), ' +
      'а ключа в кеше нет, войти по PIN заново. Иначе проверить STT ' +
      'API key аватара в админке.'
    );
  }
  return (
    `Сервер отклонил запрос с кодом ${status ?? '4xx'}. ` +
    'Проверить путь, тело запроса и права доступа; текст ответа ' +
    'сервера — в поле server лога.'
  );
}

/**
 * Тело `switch (failure.kind)` из Dart. Две причины подставляют код ответа,
 * поэтому таблица хранит функции, а не готовые строки.
 */
const HINT_BY_KIND: Record<FailureKind, (failure: AppFailure) => string> = {
  noNetwork: () =>
    'Планшет не достучался до сервера. Проверить сеть с самого устройства ' +
    '(curl с планшета), имя хоста в DNS и открыт ли порт.',
  offline: () =>
    'На планшете нет сети: Wi-Fi выключен или не подключён. Сервер тут ' +
    'ни при чём — проверить настройки Wi-Fi на самом устройстве.',
  connectTimeout: () =>
    'Соединение не установилось за отведённое время. Порт закрыт файрволом, ' +
    'планшет в другой сети или сервер перегружен.',
  sendTimeout: () =>
    'Не успели отправить тело запроса. Узкий канал отдачи или прокси ' +
    'буферизует запрос целиком.',
  receiveTimeout: () =>
    'Запрос ушёл, ответ не пришёл вовремя. Смотреть скорость обработки ' +
    'на стороне сервера.',
  serverDown: (failure) =>
    `Сервер ответил ${failure.statusCode ?? '5xx'}. Смотреть логи бекенда ` +
    'и апстрима за ним.',
  badRequest: badRequestHint,
  badContract: () =>
    'Ответ пришёл, но не того формата, которого ждёт приложение. ' +
    'Сверить с контрактом в справочнике.',
  streamBroken: () =>
    'Поток оборвался посреди передачи. Проверить прокси и таймауты ' +
    'на стороне сервера.',
  streamSilent: () =>
    'Поток открылся, но событий не было. Модель не отвечает либо прокси ' +
    'не отдаёт поток по частям.',
  cancelled: () => 'Запрос отменён самим приложением.',
  device: () =>
    'Сбой на устройстве, сеть ни при чём. Проверить разрешения приложения ' +
    'и свободное место.',
  notConfigured: () =>
    'На сервере не настроено то, без чего операция невозможна. Для ' +
    'распознавания речи: нет ни stt.api_url у аватара, ни ' +
    'transcribe_url в конфигурации планшета.',
  unknown: () => 'Причина не распознана. Смотреть полный текст в логе по коду.',
};

/**
 * `_endpointOf` — какой запрос стоит за операцией.
 *
 * `update` осталась в таблице вместе с операцией: скачивание APK из
 * веб-порта убрано, но код операции по-прежнему встречается в журналах.
 */
const ENDPOINT_BY_OP: Record<FailureOp, string> = {
  config: 'корневой адрес сервера, конфигурация',
  tablets: 'список планшетов сервера, api/v1/tablets/public',
  tabletAuth: 'вход планшета, api/v1/tablets/auth|refresh|config',
  pin: 'проверка пин-кода',
  questions: 'список вопросов-подсказок',
  transcribe: 'распознавание речи, stt.api_url аватара либо transcribe_url',
  completion: 'поток ответа модели, completion_url',
  personAnimation: 'подготовка аватара, select_language',
  personChange: 'выбор аватара, select_person',
  personMood: 'настроение аватара, animate/mood',
  update: 'скачивание обновления',
  record: 'запись с микрофона',
  playback: 'воспроизведение аудио',
  permission: 'доступ к микрофону',
};

/** Переопределения там, где общая формулировка звучит не к месту. */
function byOperation(failure: AppFailure, l10n: L10n): string | null {
  switch (failure.op) {
    case FailureOp.tabletAuth:
      // 403 — сервер отверг пароль, 429 — исчерпан лимит попыток входа.
      if (failure.statusCode === FORBIDDEN_STATUS) return l10n.tablet_password_wrong;
      if (failure.statusCode === TOO_MANY_REQUESTS_STATUS) return l10n.tablet_auth_rate_limited;
      return null;
    case FailureOp.transcribe:
      if (failure.kind === FailureKind.notConfigured) {
        return l10n.error_stt_not_configured;
      }
      // Посетитель только что говорил — ему важно, что его не услышали.
      return l10n.error_while_translating;
    case FailureOp.permission:
      return l10n.error_no_permission_access;
    case FailureOp.record:
      return l10n.cannot_record_audio;
    case FailureOp.playback:
      return l10n.error_while_playing_audio;
    case FailureOp.questions:
      // Подсказки — не главное на экране, отдельной паники не нужно.
      return l10n.something_gone_wrong;
    default:
      return null;
  }
}

/** `_byKind` — базовый текст по причине сбоя. */
const MESSAGE_KEY_BY_KIND: Record<FailureKind, L10nKey> = {
  noNetwork: 'error_connection_error',
  offline: 'error_offline',
  connectTimeout: 'error_connection_timeout',
  sendTimeout: 'error_send_timeout',
  receiveTimeout: 'error_receive_timeout',
  serverDown: 'error_server_down',
  badRequest: 'error_bad_request',
  badContract: 'error_bad_contract',
  streamBroken: 'error_stream_broken',
  streamSilent: 'error_stream_silent',
  cancelled: 'error_cancel',
  device: 'error_device',
  notConfigured: 'error_not_configured',
  unknown: 'something_gone_wrong',
};

function byKind(kind: FailureKind, l10n: L10n): string {
  return l10n[MESSAGE_KEY_BY_KIND[kind]];
}

/** Зеркало `abstract final class FailureCatalog` — вызовы читаются как в Dart. */
export const FailureCatalog = { message, canRetry, hint } as const;
